import { useEffect, useState, useMemo, Fragment } from "react";
import axios from "axios";
import Cookies from "js-cookie";
import { useHistory } from "react-router-dom";
import ImportUsersModal from "./ImportUsersModal";
import { Card } from "reactstrap";
import Loading from "../../components/utils/Loading";
import Pagination from "../../components/utils/Pagination";
import UserAccessRow from "./UserAccessRow";
import UserAccessHeader from "./UserAccessHeader";
import { useTranslation } from "react-i18next";
import PageHeader from "../../components/PageHeader";
import HowItWorksButton from "../../components/howItWorks/HowItWorksButton";
import SortingErrorsWarningIcon from "../../components/icons/SortingErrorsWarningIcon";
import StandardTable from "../../ui/components/StandardTable";

const USERS_FETCH_LIMIT = 500;

const UserAccessList = ({ corporation, viewMode }) => {
  const { t } = useTranslation();
  const history = useHistory();
  const API = process.env.REACT_APP_API_BACKEND;
  const token = Cookies.get("userToken");
  const storedUser = (() => {
    try {
      return JSON.parse(localStorage.getItem("user")) || {};
    } catch {
      return {};
    }
  })();
  const isAkanthasAccount = (storedUser?.email || "").includes("@akanthas.com");
  const canImportUsers = isAkanthasAccount && viewMode === "admin";

  const [tableData, setTableData] = useState();
  const [isLoading, setIsLoading] = useState(true);
  const [totalEntryWithoutFilter, setTotalEntryWithoutFilter] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [recordPerPage, setRecordPerPage] = useState(10);
  const [resultPage, setResultPage] = useState(0);
  const [isDeleted, setIsDeleted] = useState(false);
  const [keywordFilter, setKeywordFilter] = useState("");
  const [sortByField, setSortByField] = useState("lastName");
  const [orderSortByField, setOrderSortByField] = useState("1");
  const [license, setLicense] = useState({});
  const [showImportModal, setShowImportModal] = useState(false);

  const hasActiveFilters = useMemo(
    () => Boolean(keywordFilter),
    [keywordFilter],
  );

  const filteredUsers = useMemo(() => {
    return (tableData || []).filter((UserAccess) => {
      if (!UserAccess) return false;
      return (
        viewMode === "admin" ||
        (!UserAccess.email.includes("@akanthas.com") &&
          !UserAccess.email.includes("@demo.com"))
      );
    });
  }, [tableData, viewMode]);

  const paginationTotal = filteredUsers.length;

  const paginatedUsers = useMemo(() => {
    const start = (currentPage - 1) * recordPerPage;
    return filteredUsers.slice(start, start + recordPerPage);
  }, [filteredUsers, currentPage, recordPerPage]);

  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);
      try {
        const response = await axios.get(
          `${API}users?page=1&limit=${USERS_FETCH_LIMIT}`,
          {
            headers: {
              authorization: "Bearer " + token,
            },
            params: {
              keywordFilter: keywordFilter,
              sortByField: sortByField,
              orderSortByField: orderSortByField,
              listRoleFilter: {},
            },
          },
        );
        setTableData(response.data.data);
      } catch (error) {
        console.error(error);
        setTableData([]);
      } finally {
        setIsLoading(false);
        setIsDeleted(false);
      }
    };
    fetchData();
  }, [token, API, isDeleted, keywordFilter, sortByField, orderSortByField]);

  useEffect(() => {
    if (!token) return undefined;

    const refreshListIfVisible = async () => {
      if (document.visibilityState !== "visible") return;
      try {
        const response = await axios.get(
          `${API}users?page=1&limit=${USERS_FETCH_LIMIT}`,
          {
            headers: {
              authorization: "Bearer " + token,
            },
            params: {
              keywordFilter: keywordFilter,
              sortByField: sortByField,
              orderSortByField: orderSortByField,
              listRoleFilter: {},
            },
          },
        );
        const nextData = response.data.data;
        setTableData((prev) =>
          JSON.stringify(prev) === JSON.stringify(nextData) ? prev : nextData,
        );
      } catch (error) {
        console.error(error);
      }
    };

    document.addEventListener("visibilitychange", refreshListIfVisible);
    return () => {
      document.removeEventListener("visibilitychange", refreshListIfVisible);
    };
  }, [token, API, keywordFilter, sortByField, orderSortByField]);

  useEffect(() => {
    setCurrentPage(1);
  }, [keywordFilter, sortByField, orderSortByField, recordPerPage]);

  useEffect(() => {
    const maxPage = Math.max(
      1,
      Math.ceil(paginationTotal / recordPerPage) || 1,
    );
    if (currentPage > maxPage) {
      setCurrentPage(maxPage);
    }
  }, [paginationTotal, recordPerPage, currentPage]);

  useEffect(() => {
    if (!keywordFilter) {
      setTotalEntryWithoutFilter(filteredUsers.length);
    }
    setResultPage(Math.ceil(filteredUsers.length / recordPerPage) || 0);
  }, [filteredUsers.length, keywordFilter, recordPerPage]);

  useEffect(() => {
    if (!tableData || !corporation || keywordFilter) return;

    const filteredUserCount = tableData.filter(
      (user) =>
        !user.email.endsWith("@akanthas.com") &&
        !user.email.endsWith("@demo.com"),
    ).length;

    setLicense({
      maxUserCount: corporation.maxUserCount,
      userCount: filteredUserCount,
      userLimitReached: filteredUserCount >= corporation.maxUserCount,
    });
  }, [tableData, corporation, keywordFilter]);

  const hasUsers = paginatedUsers.length > 0;
  const hasAnyUsers = totalEntryWithoutFilter > 0;
  const isCreateDisabled =
    Boolean(license?.userLimitReached) && viewMode !== "admin";
  const userLimitReachedMessage = t("settingsUsers.userLimitReached");
  const pageTitle = t("My Accesses");
  const licenseBadge =
    license?.maxUserCount != null ? (
      <span
        className="inline-flex items-center gap-1.5 self-center whitespace-nowrap rounded-full bg-[#FBEBED] px-3 py-1 text-sm font-medium text-[#AF2F3C]"
        title={
          isCreateDisabled
            ? userLimitReachedMessage
            : t("Number of users for the contract")
        }
      >
        <SortingErrorsWarningIcon />
        {t("settingsUsers.contractUserCount", {
          count: license.userCount ?? 0,
          total: license.maxUserCount,
        })}
      </span>
    ) : null;

  const handleCreateUser = () => {
    if (license && (!license.userLimitReached || viewMode === "admin")) {
      history.push("/access/new");
    }
  };

  if (token) {
    return isLoading ? (
      <Loading color="#F5F5F5" />
    ) : (
      <div className="settings-page-wrapper">
        <PageHeader
          titleContent={
            <div className="flex items-center gap-3 flex-wrap min-w-0">
              <span className="min-w-0">{pageTitle}</span>
              {licenseBadge}
            </div>
          }
          titleContentMain={<span>{pageTitle}</span>}
          titleContentBadges={licenseBadge}
          helpButton={<HowItWorksButton topic="users" />}
        />
        <div className="settings-content-wrapper">
          <div className="settings-list-shell">
            <Card className="table border-0 shadow-none bg-white rounded-lg">
              <StandardTable
                fluidColCount={4}
                fixedColWidth={0}
                columnAlignments={["left", "left", "left", "left"]}
                topBar={
                  <section className="table-standard-top-bar">
                    <div className="table-standard-top-bar-inner">
                      <div className="flex gap-2 items-center flex-wrap flex-1 min-w-0">
                        <div className="search-input-wrapper">
                          <i className="fas fa-search search-input-icon" />
                          <input
                            type="text"
                            className="search-input"
                            value={keywordFilter}
                            placeholder={`${t("Search")} (${t("Name")}, ${t("Email")})`}
                            onChange={(event) =>
                              setKeywordFilter(event.target.value)
                            }
                          />
                        </div>
                      </div>
                      <div className="create-button-wrapper gap-2">
                        <button
                          type="button"
                          className={`create-button ${isCreateDisabled ? "disabled" : ""}`}
                          onClick={handleCreateUser}
                          disabled={isCreateDisabled}
                          title={
                            isCreateDisabled
                              ? userLimitReachedMessage
                              : undefined
                          }
                        >
                          {t("Add new user")}
                        </button>
                        {canImportUsers ? (
                          <button
                            className="create-button"
                            type="button"
                            onClick={() => setShowImportModal(true)}
                          >
                            {t("settingsUsers.importButton")}
                          </button>
                        ) : null}
                      </div>
                    </div>
                  </section>
                }
                header={
                  hasAnyUsers ? (
                    <UserAccessHeader
                      onSort={(field, order) => {
                        setSortByField(field);
                        setOrderSortByField(order);
                      }}
                      sortByField={sortByField}
                      orderSortByField={orderSortByField}
                    />
                  ) : null
                }
                bottom={
                  hasAnyUsers ? (
                    <div className="table-standard-bottom table-pagination-bottom">
                      <Pagination
                        totalEntry={paginationTotal}
                        recordPerPage={recordPerPage}
                        setRecordPerPage={setRecordPerPage}
                        currentPage={currentPage}
                        setCurrentPage={setCurrentPage}
                        resultPage={resultPage}
                        setResultPage={setResultPage}
                      />
                    </div>
                  ) : null
                }
              >
                {hasUsers ? (
                  paginatedUsers.map((user) => (
                    <Fragment key={`user-${user._id}`}>
                      <UserAccessRow
                        data={user}
                        onUnlocked={(userId) => {
                          setTableData((prev) =>
                            (prev || []).map((entry) =>
                              entry._id === userId
                                ? {
                                    ...entry,
                                    isLocked: false,
                                    failedLoginAttempts: 0,
                                    lockedUntil: null,
                                  }
                                : entry,
                            ),
                          );
                        }}
                      />
                    </Fragment>
                  ))
                ) : hasAnyUsers && hasActiveFilters ? (
                  <div className="table-standard-row table-standard-row--empty">
                    <div
                      className="table-standard-row-cell"
                      style={{ gridColumn: "span 4" }}
                    >
                      {t("No results match your search")}
                    </div>
                  </div>
                ) : null}
              </StandardTable>
            </Card>
          </div>

          <ImportUsersModal
            isOpen={showImportModal}
            onClose={() => setShowImportModal(false)}
            onImported={() => setIsDeleted(true)}
            corporation={corporation}
          />
        </div>
      </div>
    );
  }
};

export default UserAccessList;
